import React, { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Typography, IconButton, Box, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AtlasIcon } from '@diligentcorp/atlas-react'
import { AtlasButton } from 'components/atlas-material/button'
import EnhancedPreviewTemplate from './components/enhanced-preview-template'
import IpadPreviewModal from './components/ipad-preview-modal'
import {
    ColorBaseDarkGray,
    ColorBaseMulberryBlue,
    ColorBaseWhite,
    ColorEastBay
} from 'assets/styles/variables'

import NoActionIcon from 'assets/icons/NoActionIcon'
import { AddActionEditActionButton } from 'components/shared/StyledComponents'
import MinutesTemplateListComponent from 'components/shared/MinutesTemplateListComponent'
import TemplateCard from 'components/shared/TemplateCard'
import { setUserPreferredTemplate } from 'businesslayer/networkrequests/templateService'
import i18n from 'common/util/internationalization'
import { CircularProgressLoader } from 'components/global-progress-modal/global-progress-modal'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        // iPad responsive - show only right panel
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            display: 'block',
            backgroundColor: '#ffffff',
            height: '100%'
        }
    },
    leftPanel: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f2f2f2',
        padding: theme.spacing(3),
        // iPad responsive - hide left panel by default
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            display: 'none'
        }
    },
    rightPanel: {
        width: '658px',
        backgroundColor: ColorBaseWhite,
        boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // iPad responsive - full width and proper height
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            width: '100%',
            height: '100%',
            boxShadow: 'none'
        },
        // When used in drawer context, take full height
        '&.drawer-context': {
            width: '100%',
            height: '100%',
            boxShadow: 'none'
        }
    },
    sidebarHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3)
    },
    sidebarTitle: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: '22px',
        lineHeight: '26px',
        letterSpacing: '0px'
    },
    sidebarDescription: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 400,
        fontSize: '16px',
        color: '#6B7280'
    },
    closeButton: {
        '&:focus, &:active': {
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    sidebarContent: {
        flex: 1,
        padding: theme.spacing(3),
        overflowY: 'auto',
        // Improve scrolling on iPad
        '@media (min-device-width: 768px) and (max-device-width: 1024px)': {
            '-webkit-overflow-scrolling': 'touch'
        }
        // Add padding to account for footer height when footer is visible
        // paddingBottom: '80px'
    },
    subtitle: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 400,
        fontSize: '16px',
        color: '#6B7280'
        // marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: '16px',
        marginBottom: theme.spacing(2),
        color: '#1E1E1E'
    },
    sectionTitlecustom: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: '16px',
        color: '#1E1E1E'
    },
    templateOption: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2),
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        marginBottom: theme.spacing(2),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#F9FAFB'
        }
    },
    templateIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        backgroundColor: '#DC2626',
        marginRight: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    templateInfo: {
        flex: 1
    },
    templateName: {
        fontWeight: 600,
        fontSize: '16px',
        marginBottom: '4px'
    },
    templateDescription: {
        fontSize: '14px',
        color: '#6B7280'
    },
    templateTag: {
        fontSize: '12px',
        backgroundColor: '#F3F4F6',
        borderRadius: '12px',
        padding: '4px 8px',
        color: '#6B7280',
        marginLeft: 'auto'
    },
    customTemplatesSection: {
        marginTop: theme.spacing(3)
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#9CA3AF'
    },
    emptyStateIcon: {
        width: '64px',
        height: '64px',
        margin: '0 auto 16px',
        backgroundColor: '#F3F4F6',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    templateManagerLink: {
        color: ColorEastBay,
        textDecoration: 'underline',
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer'
        }
    },
    sidebarFooter: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    cancelButton: {
        textTransform: 'none',
        color: '#6B7280',
        border: '1px solid #D1D5DB',
        padding: '8px 16px',
        borderRadius: '6px',
        '&:hover': {
            backgroundColor: '#F9FAFB'
        }
    },
    saveButton: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        color: ColorBaseWhite,
        padding: '8px 16px',
        borderRadius: '6px',
        '&:hover': {
            backgroundColor: '#364262'
        },
        '&:focus': {
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    },
    radioGroup: {
        '& .MuiFormControlLabel-root': {
            margin: 0,
            width: '100%'
        }
    },
    noDataContainer: {
        borderRadius: '12px',
        display: 'flex',
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        alignSelf: 'stretch',
        placeContent: 'center',
        placeItems: 'center',
        textAlign: 'center',
        backgroundColor: ColorBaseWhite,
        height: '100%',
        minHeight: '590px'
    },
    noDataTitle: {
        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '32px',
        textAlign: 'center',
        color: ColorBaseDarkGray
    },
    saveTemplate: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        display: 'flex',
        color: '#fff',
        borderWidth: '0px',
        borderRadius: '3px',
        letterSpacing: '0.5px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        padding: '8px 12px',
        fontWeight: 600,
        placeContent: 'center',
        gap: '8px',
        minWidth: '78px',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px',
            backgroundColor: `${ColorEastBay}`
        },
        '&:hover': {
            backgroundColor: '#364262',
            color: '#fff',
            cursor: 'pointer'
        }
    },
    customTemplateDescription: {
        marginBottom: '16px',
        fontSize: '14px',
        fontFamily: 'Source Sans Pro',
        fontWeight: 400
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        flex: 1,
        minHeight: 'calc(100vh - 144px)'
    }
}))

interface TemplatePreviewPageProps {
    imageUrlsData?: string[]
    loading?: boolean
    onClose?: () => void
    onCancel?: () => void
    systemTemplate?: any
    templateOptions?: any[]
    templateOption?: any
    setTemplateOption?: (template: any) => void
    handleLoadMore?: () => void
    isLoading?: boolean
    hasMoreTemplates?: boolean
    handleGetTemplatePreview?: (templateId: string) => void
    previewSelection?: any
    setPreviewSelection?: (template: any) => void
    userPreferredTemplateResponse?: any
    isDesktop?: boolean
    hideFooter?: boolean
    isDrawerContext?: boolean
}

const TemplatePreviewPage: React.FC<TemplatePreviewPageProps> = ({
    imageUrlsData = [],
    loading = false,
    onClose,
    onCancel,
    systemTemplate,
    templateOptions,
    templateOption,
    setTemplateOption,
    handleLoadMore,
    isLoading,
    hasMoreTemplates,
    handleGetTemplatePreview,
    previewSelection,
    setPreviewSelection,
    userPreferredTemplateResponse,
    isDesktop = false,
    hideFooter = false,
    isDrawerContext = false
}) => {
    const classes = useStyles()
    const history = useHistory()
    const [selectedTemplate] = useState<string>('diligent-template') //setSelectedTemplate // TODO: Remove this if not needed
    const [isSubmitting, setIsSubmitting] = useState(false)

    // iPad specific state
    const [isIpad, setIsIpad] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)

    // Detect iPad device (only if not forced to desktop)
    useEffect(() => {
        if (isDesktop) {
            setIsIpad(false)
            return
        }

        const userAgent = navigator.userAgent.toLowerCase()
        const isIpadDevice =
            /ipad/.test(userAgent) ||
            (/macintosh/.test(userAgent) && 'ontouchend' in document) ||
            (navigator.maxTouchPoints &&
                navigator.maxTouchPoints > 2 &&
                /Macintosh/.test(userAgent))

        if (isIpadDevice) {
            setIsIpad(true)
        }
    }, [isDesktop])

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose()
        } else {
            history.goBack()
        }
    }, [onClose, history])

    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel()
        } else {
            handleClose()
        }
    }, [onCancel, handleClose])

    // iPad specific preview handler (only for Preview button clicks)
    const handleIpadPreview = useCallback(
        (templateId: string) => {
            if (isIpad) {
                // For iPad, show preview modal
                setShowPreviewModal(true)
                // Also call the original preview handler to load the template data
                if (handleGetTemplatePreview) {
                    handleGetTemplatePreview(templateId)
                }
            } else {
                // For desktop, use original preview handler
                if (handleGetTemplatePreview) {
                    handleGetTemplatePreview(templateId)
                }
            }
        },
        [isIpad, handleGetTemplatePreview]
    )

    // iPad specific row selection handler (only selects template, doesn't show preview)
    const handleIpadRowSelection = useCallback(
        (templateId: string) => {
            if (isIpad) {
                // For iPad, only select the template without showing preview
                const templates = templateOptions || []
                const combinedTemplates = systemTemplate
                    ? [systemTemplate, ...templates]
                    : templates
                const selectedTemplate = combinedTemplates.find(
                    (template) => template?.id === templateId
                )
                if (selectedTemplate && setPreviewSelection) {
                    setPreviewSelection(selectedTemplate)
                }
            } else {
                // For desktop, use original preview handler
                if (handleGetTemplatePreview) {
                    handleGetTemplatePreview(templateId)
                }
            }
        },
        [isIpad, systemTemplate, templateOptions, setPreviewSelection, handleGetTemplatePreview]
    )

    const handleClosePreviewModal = useCallback(() => {
        setShowPreviewModal(false)
    }, [])

    //TODO: Remove this if not needed
    // const handleTemplateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    //     setSelectedTemplate(event.target.value)
    // }, [])

    const handleSaveTemplate = useCallback(async () => {
        if (onCancel) {
            setIsSubmitting(true)
            onCancel()
        } else {
            // Default save behavior
            handleClose()
        }

        // Close preview modal if open on iPad
        if (isIpad && showPreviewModal) {
            setShowPreviewModal(false)
        }
        // Update the actual templateOption with the previewSelection when save is clicked
        if (previewSelection && Object.keys(previewSelection).length > 0) {
            setTemplateOption?.(previewSelection)
        }

        // Save user's preferred template with saved export option or 'minutesOnly' as fallback
        if (previewSelection && Object.keys(previewSelection).length > 0) {
            try {
                const templateId = previewSelection.id

                // Use saved export option if available,else use minutesOnly
                let exportType = 'minutesOnly' // Default fallback
                if (userPreferredTemplateResponse?.preferredExportOption) {
                    exportType = userPreferredTemplateResponse.preferredExportOption
                }

                await setUserPreferredTemplate(templateId, exportType)
            } catch (error) {
                console.error('Failed to update preferred template:', error)
            }
        }

        // Reset previewSelection after saving
        setPreviewSelection?.({})

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedTemplate,
        handleClose,
        previewSelection,
        setTemplateOption,
        setPreviewSelection,
        userPreferredTemplateResponse,
        isIpad,
        showPreviewModal
    ])

    return (
        <div className={classes.container}>
            {/* Left Panel - Document Preview (hidden on iPad) */}
            <div className={classes.leftPanel}>
                <EnhancedPreviewTemplate imageUrlsData={imageUrlsData} loading={loading} />
            </div>

            {/* Right Panel - Templates Sidebar (full width on iPad) */}
            <div className={`${classes.rightPanel} ${isDrawerContext ? 'drawer-context' : ''}`}>
                {/* Header */}
                <div className={classes.sidebarHeader}>
                    <Box display="flex" flexDirection="column">
                        <Typography className={classes.sidebarTitle}>
                            {i18n.t('MINUTES_TEMPLATES_TITLE')}
                        </Typography>
                        <Typography className={classes.subtitle}>
                            {i18n.t('MINUTES_TEMPLATES_TEMPLATE_FORMATTING')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        className={classes.closeButton}
                        size="small"
                        aria-label="Close"
                    >
                        <AtlasIcon name="close" />
                    </IconButton>
                </div>

                {/* Content */}
                <div className={classes.sidebarContent}>
                    {isLoading ? (
                        <Box className={classes.loaderContainer}>
                            <CircularProgressLoader />
                        </Box>
                    ) : (
                        <>
                            <Typography className={classes.sectionTitle}>
                                {i18n.t('MINUTES_TEMPLATES_SYSTEM_TEMPLATE')}
                            </Typography>

                            {systemTemplate && (
                                <TemplateCard
                                    template={systemTemplate}
                                    onPreview={
                                        isIpad
                                            ? handleIpadRowSelection
                                            : handleGetTemplatePreview || (() => {})
                                    }
                                    onPreviewButton={isIpad ? handleIpadPreview : undefined}
                                    withRadio={true}
                                    templateOption={templateOption}
                                    previewSelection={previewSelection}
                                    setPreviewSelection={setPreviewSelection}
                                    showPreview={isIpad} // Show preview button on iPad
                                />
                            )}

                            {/* Custom Templates Section */}
                            <div className={classes.customTemplatesSection}>
                                <Typography className={classes.sectionTitlecustom}>
                                    {i18n.t('MINUTES_TEMPLATES_CUSTOM_TEMPLATE')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    className={classes.customTemplateDescription}
                                >
                                    {i18n.t('MINUTES_TEMPLATES_CUSTOM_TEMPLATES_DESCRIPTION')}{' '}
                                    {i18n.t('MINUTES_TEMPLATES_NO_TEMPLATES_DESCRIPTION')}{' '}
                                    <span
                                        role="link"
                                        tabIndex={0}
                                        className={classes.templateManagerLink}
                                        onClick={() => history.push('/templates')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                history.push('/templates')
                                            }
                                        }}
                                    >
                                        {i18n.t('MINUTES_TEMPLATES_GO_TO_TEMPLATE_MANAGER_CTA')}
                                    </span>
                                </Typography>
                                {templateOptions && templateOptions.length > 0 ? (
                                    <MinutesTemplateListComponent
                                        templateOptions={templateOptions}
                                        templateOption={templateOption}
                                        handleLoadMore={handleLoadMore}
                                        isLoading={isLoading}
                                        hasMoreTemplates={hasMoreTemplates}
                                        handleGetTemplatePreview={
                                            isIpad
                                                ? handleIpadRowSelection
                                                : handleGetTemplatePreview || (() => {})
                                        }
                                        handlePreviewButton={isIpad ? handleIpadPreview : undefined}
                                        previewSelection={previewSelection}
                                        setPreviewSelection={setPreviewSelection}
                                        showPreview={isIpad} // Show preview button on iPad
                                    />
                                ) : (
                                    <Box className={classes.noDataContainer}>
                                        <NoActionIcon />
                                        <Typography variant="body1" className={classes.noDataTitle}>
                                            {i18n.t('MINUTES_TEMPLATES_NO_TEMPLATES')}
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {!hideFooter && (
                    <div className={classes.sidebarFooter}>
                        <AddActionEditActionButton
                            key="actionCancel"
                            data-analytics="MM-Template-Cancel"
                            disableFocusRipple
                            onClick={handleCancel}
                            disableRipple
                        >
                            {i18n.t('CANCEL')}
                        </AddActionEditActionButton>
                        <AtlasButton
                            data-analytics="MM-Template-Save"
                            role="button"
                            onKeyPress={() => {
                                console.log('onKeyPress')
                            }}
                            disableRipple
                            className={classes.saveTemplate}
                            key="actionSave"
                            disabled={isSubmitting}
                            type="submit"
                            onClick={handleSaveTemplate}
                            disableFocusRipple
                        >
                            {isSubmitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                i18n.t('SAVE')
                            )}
                        </AtlasButton>
                    </div>
                )}
            </div>

            {/* iPad Preview Drawer */}
            {isIpad && (
                <IpadPreviewModal
                    open={showPreviewModal}
                    onClose={handleClosePreviewModal}
                    systemTemplate={systemTemplate}
                    previewSelection={previewSelection}
                    imageUrlsData={imageUrlsData}
                    loading={loading}
                />
            )}
        </div>
    )
}

export default TemplatePreviewPage
